
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { StarRating } from './StarRating';
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Review {
  id: string;
  user_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  user_profile?: {
    full_name?: string;
    display_name?: string;
  } | null;
}

interface ReviewsListProps {
  studioId: string;
  limit?: number;
  showSorting?: boolean;
}

export const ReviewsList = ({
  studioId,
  limit = 5,
  showSorting = true
}: ReviewsListProps) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>('newest');
  const { toast } = useToast();

  // Load reviews and user profiles in one query
  const fetchReviews = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('reviews')
        .select(`
          id, 
          user_id, 
          rating, 
          comment, 
          created_at,
          profiles:user_id (full_name, display_name)
        `) as any;

      // Apply filters with type assertions
      query = query.eq('studio_id', studioId as any);
      query = query.eq('is_published', true as any);

      // Apply sorting
      if (sortBy === 'highest') {
        query = query.order('rating', { ascending: false });
      } else if (sortBy === 'lowest') {
        query = query.order('rating', { ascending: true });
      } else {
        // Default to newest
        query = query.order('created_at', { ascending: false });
      }

      // Apply limit
      if (limit > 0) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Map results to include user profile info
      const reviewsWithUserInfo = data.map((review: any) => ({
        ...review,
        user_profile: review.profiles
      }));

      setReviews(reviewsWithUserInfo as Review[]);
    } catch (error: any) {
      console.error("Error fetching reviews:", error);
      toast({
        title: "Fehler",
        description: "Bewertungen konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch reviews when studioId or sorting changes
  useEffect(() => {
    fetchReviews();
  }, [studioId, sortBy]);

  // Helper to format user display name
  const formatUserName = (review: Review) => {
    if (review.user_profile?.full_name) {
      // Display first name + first letter of last name
      const nameParts = review.user_profile.full_name.split(' ');
      if (nameParts.length > 1) {
        return `${nameParts[0]} ${nameParts[1].charAt(0)}.`;
      }
      return review.user_profile.full_name;
    } else if (review.user_profile?.display_name) {
      return review.user_profile.display_name;
    }
    // Fallback to user ID with first 4 chars
    return `Kunde ${review.user_id.substring(0, 4)}`;
  };

  // Helper to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Kundenbewertungen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Kundenbewertungen</CardTitle>
        {showSorting && (
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Sortieren nach" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Neueste</SelectItem>
              <SelectItem value="highest">Beste</SelectItem>
              <SelectItem value="lowest">Kritischste</SelectItem>
            </SelectContent>
          </Select>
        )}
      </CardHeader>
      <CardContent>
        {reviews.length > 0 ? (
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="font-medium">{formatUserName(review)}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(review.created_at)}
                  </div>
                </div>
                <StarRating rating={review.rating} />
                {review.comment && (
                  <p className="text-sm text-muted-foreground mt-2">{review.comment}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground">
            Noch keine Bewertungen für dieses Studio.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
